<template>
    <v-dialog
        :value="value"
        @input="$emit('input', $event)"
        max-width="800"
        scrollable
        persistent
        :fullscreen="phoneOnly"
        :transition="phoneOnly ? 'dialog-bottom-transition' : 'dialog-transition'"
    >
        <v-card
            v-if="enrollment"
            :loading="loadingConfig"
            :class="{'fill-height d-flex flex-column': phoneOnly}"
            :style="iosSafeArea ? 'padding-top: 45px!important;' : ''"
        >
            <v-card-title class="d-flex align-start" :class="{'px-4 py-3': phoneOnly, 'py-4': pcOnly}">
                <v-icon class="me-2 mt-1" color="primary" :small="phoneOnly">mdi-file-document-edit-outline</v-icon>
                <div class="flex-grow-1" style="min-width: 0;">
                    <div :class="phoneOnly ? 'subtitle-1 font-weight-medium' : ''" style="line-height: 1.2;">
                        {{ isResubmit ? 'Edit & Resubmit' : 'Quarterly Report' }}
                    </div>
                    <div class="caption muted--text mt-1" style="white-space: normal; word-break: break-word; line-height: 1.3;">
                        {{ enrollment.user && enrollment.user.name }} — {{ enrollment.course && enrollment.course.name }}
                    </div>
                    <v-alert v-if="isResubmit" type="warning" dense outlined class="mt-2 mb-0 caption" icon="mdi-undo-variant">
                        This report was sent back. Edit at least one field before re-submitting.
                    </v-alert>
                </div>
                <v-btn icon @click="close" :disabled="loading" class="ms-2">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider />

            <v-card-text v-if="config" class="pt-4 qr-form" :class="{'px-4': phoneOnly, 'flex-grow-1': phoneOnly}">
                <!-- Report generator (always visible) -->
                <div class="ai-draft-box mb-2">
                    <div class="ai-draft-desc">
                        <template v-if="formMode === 'ai'">Review the draft below and set the star ratings yourself before submitting.</template>
                        <template v-else>Reads this student's last 3 months of lessons and drafts the report for you — you just review it and set the star ratings before submitting.</template>
                    </div>
                    <v-btn
                        block
                        color="primary"
                        depressed
                        :loading="generating"
                        :disabled="loading || loadingLessons || !lessons.length || draftGenerated"
                        class="ai-generate-btn"
                        @click="generateDraft"
                    >
                        <v-icon left small>mdi-creation</v-icon>
                        {{ draftGenerated ? 'Report generated' : 'Generate quarterly report from these lessons' }}
                    </v-btn>
                    <div class="ai-draft-actions">
                        <template v-if="!formShown">
                            <v-btn v-if="lastMode" x-small text color="primary" class="manual-link" :disabled="generating" @click="backToReport">
                                <v-icon x-small left>mdi-arrow-left</v-icon>Back to your report
                            </v-btn>
                            <v-btn v-else x-small text color="primary" class="manual-link" :disabled="generating" @click="fillManually">
                                <v-icon x-small left>mdi-pencil-outline</v-icon>Prefer to write it yourself? Fill in manually
                            </v-btn>
                        </template>
                        <v-btn v-else x-small text color="primary" class="manual-link" @click="viewLessons">
                            <v-icon x-small left>mdi-format-list-bulleted</v-icon>View the lessons
                        </v-btn>
                    </div>
                </div>

                <v-divider class="my-4" />

                <!-- Lesson reviews — the default view, until the teacher generates or chooses manual -->
                <template v-if="!formShown">
                    <div class="reviews-head">
                        <label class="field-label">Last 3 months of lessons</label>
                        <span v-if="lessons.length" class="reviews-count">
                            {{ lessons.length }} {{ lessons.length === 1 ? 'lesson' : 'lessons' }}<template v-if="lessonsAvg"> &middot; avg {{ lessonsAvg.toFixed(1) }}/5</template>
                        </span>
                    </div>
                    <div v-if="generating" class="reviews-generating">
                        <v-progress-circular indeterminate color="primary" size="42" width="3" />
                        <div class="mt-3">Reading {{ lessons.length }} {{ lessons.length === 1 ? 'lesson' : 'lessons' }} and drafting the report…</div>
                        <div class="caption muted--text mt-1">This usually takes a few seconds.</div>
                    </div>
                    <div v-else-if="loadingLessons" class="text-center py-8">
                        <v-progress-circular indeterminate color="primary" />
                    </div>
                    <div v-else-if="lessons.length" class="lessons-scroll">
                        <div v-for="(l, i) in lessons" :key="i" class="lesson-card">
                            <div class="lesson-top">
                                <span class="lesson-date">{{ l.date }}</span>
                                <v-rating
                                    :value="Math.round(l.rating || 0)"
                                    readonly dense length="5" size="18"
                                    color="amber darken-2" background-color="grey lighten-2"
                                    class="lesson-stars"
                                />
                            </div>
                            <div class="lesson-activity">{{ l.class_activity }}</div>
                            <div v-if="l.homework" class="lesson-hw">HW: {{ l.homework }}</div>
                            <div v-if="l.teacher_notes" class="lesson-notes">{{ l.teacher_notes }}</div>
                        </div>
                    </div>
                    <div v-else class="reviews-empty">
                        <v-icon color="muted lighten-1" size="40">mdi-clipboard-text-off-outline</v-icon>
                        <div class="mt-2">No lesson reports found in the last 3 months yet.</div>
                    </div>
                </template>

                <!-- Report form — shown after generating a draft, choosing manual, or when resubmitting -->
                <template v-else>

                <!-- Student Level (direct question) -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">What's your student's level? *</label>
                    </div>
                    <v-select
                        :value="form.level"
                        @change="setLevel"
                        :items="levelOptions"
                        outlined dense
                        hide-details="auto"
                        placeholder="Select a level"
                        prepend-inner-icon="mdi-stairs-up"
                        :error="submitted && !form.level"
                        :error-messages="submitted && !form.level ? ['Please select a level.'] : []"
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Progress Summary -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Progress Summary *</label>
                        <v-btn v-if="formMode === 'manual' && config.templates && config.templates.progress_summary" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('progress_summary')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.progress_summary"
                        outlined dense :rows="phoneOnly ? 4 : 5" hide-details
                        placeholder="Describe the student's progress this quarter..."
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Topics Covered -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Topics Covered *</label>
                        <v-btn v-if="formMode === 'manual' && config.templates && config.templates.topics_covered" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('topics_covered')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <div v-for="(topic, i) in form.topics_covered" :key="i" class="d-flex align-center mb-2">
                        <v-text-field v-model="form.topics_covered[i]" outlined dense hide-details :placeholder="`Topic ${i + 1}`" class="me-2" style="min-width: 0;" />
                        <v-btn icon small color="error" @click="removeTopic(i)" :disabled="form.topics_covered.length <= 1" class="flex-shrink-0">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-btn x-small text color="primary" class="mt-1" @click="addTopic">
                        <v-icon x-small left>mdi-plus</v-icon>Add topic
                    </v-btn>
                </div>

                <v-divider class="my-4" />

                <!-- Performance Areas -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Performance Areas *</label>
                        <span class="caption muted--text template-btn">Scale: 1 (poor) – 5 (excellent)</span>
                    </div>
                    <div
                        v-for="area in config.performance_areas"
                        :key="area"
                        class="mb-3"
                        :class="phoneOnly ? '' : 'd-flex align-center justify-space-between'"
                    >
                        <div class="text--text" :class="{'mb-1': phoneOnly}" style="word-break: break-word;">{{ area }}</div>
                        <div class="d-flex align-center">
                            <span class="me-2 caption muted--text" style="min-width: 12px;">{{ form.performance_areas[area] || 0 }}</span>
                            <v-rating
                                :value="form.performance_areas[area] || 0"
                                @input="setRating(area, $event)"
                                color="primary"
                                background-color="muted lighten-2"
                                hover length="5" dense
                                :size="phoneOnly ? 28 : 22"
                            />
                        </div>
                    </div>
                    <v-card flat outlined class="mt-3 pa-3">
                        <div class="d-flex align-center justify-space-between flex-wrap" style="row-gap: 8px;">
                            <div>
                                <div class="caption muted--text">Average score</div>
                                <div class="font-weight-bold" style="font-size: 1.3rem">{{ averageScore.toFixed(2) }} / 5</div>
                            </div>
                            <v-chip :color="statusColor" dark label>{{ statusLabel }}</v-chip>
                        </div>
                    </v-card>
                </div>

                <v-divider class="my-4" />

                <!-- Areas for Improvement -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Areas for Improvement *</label>
                        <v-btn v-if="formMode === 'manual' && config.templates && config.templates.areas_for_improvement" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('areas_for_improvement')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.areas_for_improvement"
                        outlined dense :rows="phoneOnly ? 4 : 4" hide-details
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Next Phase Plan -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Next Phase Plan *</label>
                        <v-btn v-if="formMode === 'manual' && config.templates && config.templates.next_phase_plan" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('next_phase_plan')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.next_phase_plan"
                        outlined dense :rows="phoneOnly ? 4 : 3" hide-details
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Teacher's Message -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Teacher's Message *</label>
                        <v-btn v-if="formMode === 'manual' && config.templates && config.templates.teachers_message" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('teachers_message')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.teachers_message"
                        outlined dense :rows="phoneOnly ? 4 : 3" hide-details
                    />
                </div>
                </template>
            </v-card-text>
            <v-card-text v-else-if="loadingConfig" class="text-center py-6">
                <v-progress-circular indeterminate color="primary" />
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-4 py-3" :class="{'pb-safe': iosSafeArea}">
                <template v-if="phoneOnly">
                    <v-btn text @click="close" :disabled="loading || generating">Cancel</v-btn>
                    <v-spacer />
                    <v-btn v-if="formShown" color="primary" depressed :loading="loading" :disabled="!canSubmit || generating" @click="submit">
                        {{ isResubmit ? 'Resubmit' : 'Submit Report' }}
                    </v-btn>
                </template>
                <template v-else>
                    <v-spacer />
                    <v-btn text @click="close" :disabled="loading || generating">Cancel</v-btn>
                    <v-btn v-if="formShown" color="primary" :loading="loading" :disabled="!canSubmit || generating" @click="submit">{{ isResubmit ? 'Resubmit' : 'Submit Report' }}</v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';

function blankForm() {
    return {
        level: null,
        progress_summary: '',
        topics_covered: ['', '', ''],
        performance_areas: {},
        areas_for_improvement: '',
        next_phase_plan: '',
        teachers_message: '',
    };
}

export default {
    props: {
        value: { type: Boolean, default: false },
        enrollment: { type: Object, default: null }, // {enrollment_id, user, course, category}
    },
    data() {
        return {
            form: blankForm(),
            config: null,
            loadingConfig: false,
            loading: false,
            generating: false,
            submitted: false,
            lessons: [],
            loadingLessons: false,
            formMode: null,      // null = show lesson reviews; 'ai' = AI-drafted form; 'manual' = blank form
            lastMode: null,      // the form mode to return to when peeking back at the lessons
            draftGenerated: false, // once an AI draft is generated, the Generate button stays disabled
            levelOptions: [
                { text: 'Beginner', value: 'beginner' },
                { text: 'Intermediate', value: 'intermediate' },
                { text: 'Advanced', value: 'advanced' },
            ],
        };
    },
    computed: {
        ...GlobalComputed,
        isResubmit() {
            return !!(this.enrollment && this.enrollment.reversed_report);
        },
        formShown() {
            return this.formMode !== null; // reviews hidden, form visible
        },
        thresholds() {
            return (this.config && this.config.status_thresholds) || { on_track: 4.0, needs_support: 2.5 };
        },
        filledTopicsCount() {
            return (this.form.topics_covered || []).filter(t => t && t.trim()).length;
        },
        lessonsAvg() {
            const rated = (this.lessons || []).map(l => Number(l.rating)).filter(v => !Number.isNaN(v) && v > 0);
            if (!rated.length) return null;
            return rated.reduce((a, b) => a + b, 0) / rated.length;
        },
        averageScore() {
            const values = Object.values(this.form.performance_areas || {})
                .map(v => Number(v))
                .filter(v => !Number.isNaN(v) && v > 0);
            if (!values.length) return 0;
            return values.reduce((a, b) => a + b, 0) / values.length;
        },
        studentStatus() {
            const avg = this.averageScore;
            if (avg >= this.thresholds.on_track) return 'on_track';
            if (avg >= this.thresholds.needs_support) return 'needs_support';
            return 'at_risk';
        },
        statusLabel() {
            return ({ on_track: 'On Track', needs_support: 'Needs Support', at_risk: 'At Risk' })[this.studentStatus];
        },
        statusColor() {
            return ({ on_track: 'success', needs_support: 'warning', at_risk: 'error' })[this.studentStatus];
        },
        canSubmit() {
            if (!this.config) return false;
            const allRated = (this.config.performance_areas || []).every(area => Number(this.form.performance_areas[area] || 0) > 0);
            const filled = f => !!(this.form[f] && String(this.form[f]).trim());
            // No length limits — just require each field to have some content.
            return (
                !!this.form.level &&
                filled('progress_summary') &&
                filled('areas_for_improvement') &&
                filled('next_phase_plan') &&
                filled('teachers_message') &&
                this.filledTopicsCount >= 1 &&
                allRated
            );
        },
    },
    watch: {
        value(open) {
            if (open && this.enrollment) {
                this.reset();
                this.fetchConfig();
                this.fetchLessons();
            }
        },
    },
    methods: {
        ...GlobalMethods,
        reset() {
            this.form = blankForm();
            this.config = null;
            this.submitted = false;
            this.loading = false;
            this.generating = false;
            this.lessons = [];
            this.loadingLessons = false;
            this.lastMode = null;
            this.draftGenerated = false;
            // Resubmitting a sent-back report? Go straight to the (pre-filled) form,
            // in manual mode so the teacher can use templates while editing.
            this.formMode = this.isResubmit ? 'manual' : null;
        },
        fillManually() {
            this.formMode = 'manual'; // reveal the blank form + template helpers
        },
        viewLessons() {
            // Peek back at the lessons without losing the report (form data persists).
            this.lastMode = this.formMode;
            this.formMode = null;
        },
        backToReport() {
            this.formMode = this.lastMode || 'manual';
        },
        useTemplate(field) {
            const tpl = this.config && this.config.templates && this.config.templates[field];
            if (!tpl) return;
            if (Array.isArray(tpl)) {
                this.$set(this.form, field, [...tpl]);
            } else {
                this.$set(this.form, field, tpl);
            }
        },
        fetchLessons() {
            if (!this.enrollment) return;
            this.loadingLessons = true;
            this.$store.dispatch('loadQuarterlyLessons', { enrollmentId: this.enrollment.enrollment_id })
                .then(lessons => { this.lessons = lessons || []; })
                .catch(() => { this.lessons = []; })
                .finally(() => { this.loadingLessons = false; });
        },
        close() {
            if (this.loading || this.generating) return;
            this.$emit('input', false);
        },
        generateDraft() {
            if (!this.enrollment || this.generating) return;
            // Warn before replacing any text the teacher already has (typed or resubmit pre-fill).
            const hasContent = !!(
                this.form.progress_summary || this.form.areas_for_improvement ||
                this.form.next_phase_plan || this.form.teachers_message ||
                (this.form.topics_covered || []).some(t => t && t.trim())
            );
            if (hasContent && !window.confirm('This will replace the text fields with a freshly generated draft. Your star ratings are kept. Continue?')) {
                return;
            }
            this.generating = true;
            this.$store.dispatch('generateQuarterlyDraft', { enrollmentId: this.enrollment.enrollment_id })
                .then(d => {
                    if (d.level) this.$set(this.form, 'level', d.level);
                    this.$set(this.form, 'progress_summary', d.progress_summary || '');
                    if (d.topics_covered && d.topics_covered.length) {
                        this.$set(this.form, 'topics_covered', [...d.topics_covered]);
                    }
                    this.$set(this.form, 'areas_for_improvement', d.areas_for_improvement || '');
                    this.$set(this.form, 'next_phase_plan', d.next_phase_plan || '');
                    this.$set(this.form, 'teachers_message', d.teachers_message || '');
                    // performance_areas left untouched — stars stay manual
                    // The form + inline helper are the confirmation — no toast (it overlaps the footer).
                    this.draftGenerated = true; // one-shot: the Generate button stays disabled afterwards
                    this.formMode = 'ai'; // reveal the form for review/edit
                })
                .catch(error => {
                    const msg = (error && error.response && error.response.data && error.response.data.message) || 'Could not generate a draft. Please try again.';
                    this.showMsg(msg, 'error');
                })
                .finally(() => { this.generating = false; });
        },
        fetchConfig() {
            if (!this.enrollment) return;
            this.loadingConfig = true;
            this.$store.dispatch('loadQuarterlyFormConfig', this.enrollment.enrollment_id)
                .then(response => {
                    this.config = response.data;
                    const areas = (this.config.performance_areas || []);
                    const reversed = this.enrollment && this.enrollment.reversed_report;
                    if (reversed) {
                        // Pre-fill with the report the admin sent back so the teacher edits in place.
                        // Keep existing rating keys if they match the current config; fall back to 0 for any new area.
                        const performance = {};
                        areas.forEach(a => { performance[a] = Number((reversed.performance_areas || {})[a]) || 0; });
                        this.$set(this.form, 'performance_areas', performance);
                        this.$set(this.form, 'level',                 reversed.level || null);
                        this.$set(this.form, 'progress_summary',      reversed.progress_summary || '');
                        this.$set(this.form, 'topics_covered',        (reversed.topics_covered && reversed.topics_covered.length) ? [...reversed.topics_covered] : ['', '', '']);
                        this.$set(this.form, 'areas_for_improvement', reversed.areas_for_improvement || '');
                        this.$set(this.form, 'next_phase_plan',       reversed.next_phase_plan || '');
                        this.$set(this.form, 'teachers_message',      reversed.teachers_message || '');
                    } else {
                        const performance = {};
                        areas.forEach(a => { performance[a] = 0; });
                        this.$set(this.form, 'performance_areas', performance);
                    }
                })
                .catch(() => { this.showMsg('Failed to load report form.', 'error'); })
                .finally(() => { this.loadingConfig = false; });
        },
        addTopic() { this.form.topics_covered.push(''); },
        removeTopic(i) { this.form.topics_covered.splice(i, 1); },
        setRating(area, val) { this.$set(this.form.performance_areas, area, val); },
        setLevel(val) { this.$set(this.form, 'level', val); },
        submit() {
            this.submitted = true;
            if (!this.canSubmit || !this.enrollment) return;
            this.loading = true;
            const payload = {
                level: this.form.level,
                progress_summary: this.form.progress_summary,
                topics_covered: (this.form.topics_covered || []).filter(t => t && t.trim()),
                performance_areas: this.form.performance_areas,
                areas_for_improvement: this.form.areas_for_improvement,
                next_phase_plan: this.form.next_phase_plan,
                teachers_message: this.form.teachers_message,
            };
            const action = this.isResubmit ? 'resubmitQuarterlyReport' : 'submitQuarterlyReport';
            const args = this.isResubmit
                ? { reportId: this.enrollment.reversed_report.id, data: payload }
                : { enrollmentId: this.enrollment.enrollment_id, data: payload };
            this.$store.dispatch(action, args)
                .then(() => {
                    this.$emit('submitted');
                    this.$emit('input', false);
                })
                .catch(error => {
                    const msg = (error && error.response && error.response.data && error.response.data.message) || 'Failed to submit report.';
                    this.showMsg(msg, 'error');
                })
                .finally(() => { this.loading = false; });
        },
    },
};
</script>

<style scoped>
.pb-safe {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px) !important;
}
.qr-form .ai-draft-box {
    background: #f4f9f6;
    border: 1px solid #d5e8dd;
    border-radius: 10px;
    padding: 14px 16px;
}
.qr-form .ai-draft-desc {
    font-size: 13px;
    color: #7c8a80;
    line-height: 1.45;
    margin-bottom: 12px;
}
.qr-form .ai-generate-btn {
    height: auto !important;
    min-height: 46px;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    font-weight: 700;
    letter-spacing: 0.3px;
}
/* Let the long label wrap instead of overflowing on narrow screens */
.qr-form .ai-generate-btn ::v-deep .v-btn__content {
    white-space: normal;
    line-height: 1.25;
}
.qr-form .ai-draft-actions {
    margin-top: 8px;
    text-align: center;
}
.qr-form .manual-link {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
    padding-left: 0 !important;
}
.qr-form .reviews-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}
.qr-form .reviews-count {
    font-size: 12px;
    font-weight: 600;
    color: #8a7a66;
    white-space: nowrap;
}
.qr-form .lessons-scroll {
    max-height: 46vh;
    overflow-y: auto;
    margin: 0 -4px;
    padding: 2px 6px;
}
.qr-form .lesson-card {
    background: #fff9f0;
    border: 1px solid #ece0cc;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 12px;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.qr-form .lesson-card:hover {
    border-color: #e2cfa8;
    box-shadow: 0 3px 10px rgba(60, 37, 23, 0.06);
}
.qr-form .lesson-card:last-child {
    margin-bottom: 2px;
}
.qr-form .lesson-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}
.qr-form .lesson-date {
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.3px;
    color: #3c2517;
}
.qr-form .lesson-stars {
    flex-shrink: 0;
}
.qr-form .lesson-activity {
    font-size: 14px;
    color: #4a3b2c;
    line-height: 1.45;
    word-break: break-word;
}
.qr-form .lesson-hw,
.qr-form .lesson-notes {
    font-size: 12.5px;
    color: #9a8a73;
    margin-top: 5px;
    line-height: 1.4;
    word-break: break-word;
}
.qr-form .reviews-empty {
    text-align: center;
    padding: 44px 20px;
    color: #9a8a73;
    font-size: 13px;
}
.qr-form .reviews-generating {
    text-align: center;
    padding: 48px 20px;
    color: #4a3b2c;
    font-size: 14px;
    font-weight: 500;
}
.qr-form .field-group {
    margin-bottom: 4px;
}
.qr-form .field-label-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
}
.qr-form .field-label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.qr-form .template-btn {
    margin-left: auto !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
    height: 22px !important;
}
</style>
